import React, { useState, useEffect } from "react";
import { Offcanvas } from "react-bootstrap";
import { IoIosArrowBack } from "react-icons/io";
import {
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  Autocomplete,
  TextField,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";

const ResourceEarningFilter = ({
  show,
  onClose,
  userList = [],
  selectedUserId = "",
  datePreset = "Last 3 months",
  fromDate = "",
  toDate = "",
  onApply,
  onClear,
  isEarningsFetching = false,
}) => {
  const [drawerUserId, setDrawerUserId] = useState(selectedUserId || "");
  const [drawerDatePreset, setDrawerDatePreset] = useState(datePreset || "Last 3 months");
  const [drawerFromDate, setDrawerFromDate] = useState(fromDate || "");
  const [drawerToDate, setDrawerToDate] = useState(toDate || "");

  // Sync draft states whenever drawer is opened
  useEffect(() => {
    if (show) {
      setDrawerUserId(selectedUserId || "");
      setDrawerDatePreset(datePreset || "Last 3 months");
      setDrawerFromDate(fromDate || "");
      setDrawerToDate(toDate || "");
    }
  }, [show, selectedUserId, datePreset, fromDate, toDate]);

  const handleDrawerPresetChange = (e) => {
    const preset = e.target.value;
    setDrawerDatePreset(preset);
    let from = "";
    let to = "";
    if (preset === "This month") {
      from = dayjs().startOf("month").format("YYYY-MM-DD");
      to = dayjs().endOf("month").format("YYYY-MM-DD");
    } else if (preset === "Last 3 months") {
      from = dayjs().subtract(2, "month").startOf("month").format("YYYY-MM-DD");
      to = dayjs().endOf("month").format("YYYY-MM-DD");
    } else if (preset === "Last 6 months") {
      from = dayjs().subtract(5, "month").startOf("month").format("YYYY-MM-DD");
      to = dayjs().endOf("month").format("YYYY-MM-DD");
    } else if (preset === "This year") {
      from = dayjs().startOf("year").format("YYYY-MM-DD");
      to = dayjs().endOf("year").format("YYYY-MM-DD");
    } else if (preset === "All time") {
      from = "";
      to = "";
    }
    setDrawerFromDate(from);
    setDrawerToDate(to);
  };

  const handleDrawerClearFilter = () => {
    setDrawerUserId("");
    setDrawerDatePreset("Last 3 months");
    const from = dayjs().subtract(2, "month").startOf("month").format("YYYY-MM-DD");
    const to = dayjs().endOf("month").format("YYYY-MM-DD");
    setDrawerFromDate(from);
    setDrawerToDate(to);
    if (onClear) {
      onClear();
    }
  };

  const handleSubmit = (e) => {
    if (e) e.preventDefault();
    if (onApply) {
      onApply({
        userId: drawerUserId,
        datePreset: drawerDatePreset,
        fromDate: drawerFromDate,
        toDate: drawerToDate,
      });
    }
  };

  return (
    <Offcanvas
      show={show}
      onHide={onClose}
      placement="end"
      style={{ width: window.innerWidth <= 768 ? "80%" : "50%" }}
    >
      {/* Top Header */}
      <div className="d-flex align-items-center justify-content-between filter-h-bg px-4 py-3">
        <button
          type="button"
          onClick={onClose}
          className="R_btn"
        >
          <div className="d-flex text-rgba-gray align-items-center">
            <IoIosArrowBack />
            Filter
          </div>
        </button>
        <div>
          <button
            type="button"
            onClick={handleDrawerClearFilter}
            className="R_btn tex-tgba-red clear-filter-btn"
          >
            Clear Filter
          </button>
        </div>
      </div>

      {/* Drawer Body: Form */}
      <Offcanvas.Body className="p-0 position-relative">
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <form
            onSubmit={handleSubmit}
            style={{ height: "100%", display: "flex", flexDirection: "column" }}
          >
            {/* Scrollable Form Body */}
            <div
              className="bg-white px-4 py-3"
              style={{ flex: 1, overflowY: "auto", paddingBottom: "90px" }}
            >
              <div className="row">
                {/* 1. Resource Selector (md-12) */}
                <div className="col-12 col-md-12 mb-3">
                  <label className="form-label">
                    Resource
                  </label>
                  <Autocomplete
                    id="drawer-resource-filter"
                    options={userList}
                    size="small"
                    fullWidth
                    value={
                      userList.find(
                        (u) => Number(u.user_id || u.id) === Number(drawerUserId)
                      ) || null
                    }
                    onChange={(_, newValue) => {
                      setDrawerUserId(newValue ? (newValue.user_id || newValue.id) : "");
                    }}
                    getOptionLabel={(option) => option?.name || ""}
                    isOptionEqualToValue={(option, val) =>
                      Number(option?.user_id || option?.id) === Number(val?.user_id || val?.id)
                    }
                    filterOptions={(options, { inputValue }) => {
                      const query = (inputValue || "").toLowerCase().trim();
                      if (!query) return options;
                      return options.filter((opt) =>
                        (opt?.name || "").toLowerCase().includes(query)
                      );
                    }}
                    renderOption={(props, option) => (
                      <li
                        {...props}
                        key={option.user_id || option.id}
                        style={{
                          fontSize: "14px",
                          minHeight: "36px",
                          padding: "6px 12px",
                          fontFamily: "inherit",
                        }}
                      >
                        {option.name}
                      </li>
                    )}
                    noOptionsText="No resources found"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={drawerUserId ? "" : "Select & search resource"}
                        size="small"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            height: "38px",
                            backgroundColor: "#fff",
                            fontSize: "14px",
                          },
                        }}
                      />
                    )}
                  />
                </div>

                {/* 2. Date Range Preset (md-12) */}
                <div className="col-12 col-md-12 mb-3">
                  <label className="form-label">
                    Date Range
                  </label>
                  <FormControl fullWidth size="small">
                    <Select
                      value={drawerDatePreset}
                      onChange={handleDrawerPresetChange}
                      IconComponent={ExpandMore}
                      displayEmpty
                      size="small"
                      sx={{
                        height: "38px",
                        backgroundColor: "#fff",
                      }}
                      MenuProps={{
                        anchorOrigin: {
                          vertical: "bottom",
                          horizontal: "left",
                        },
                        transformOrigin: {
                          vertical: "top",
                          horizontal: "left",
                        },
                        PaperProps: {
                          sx: { maxHeight: 280, mt: 0.5 },
                        },
                      }}
                      renderValue={(selected) =>
                        selected ? selected : <span className="lightText">Select range</span>
                      }
                    >
                      <MenuItem value="All time">All time</MenuItem>
                      <MenuItem value="This month">This month</MenuItem>
                      <MenuItem value="Last 3 months">Last 3 months</MenuItem>
                      <MenuItem value="Last 6 months">Last 6 months</MenuItem>
                      <MenuItem value="This year">This year</MenuItem>
                      <MenuItem value="Custom date">Custom date</MenuItem>
                    </Select>
                  </FormControl>
                </div>

                {/* 3. Start Date & End Date (md-6, md-6) */}
                <div className="col-12 col-md-6 mb-3">
                  <label className="form-label">
                    Start Date
                  </label>
                  
                  <DatePicker
                    value={drawerFromDate ? dayjs(drawerFromDate) : null}
                    format="DD-MM-YYYY"
                    onChange={(newValue) => {
                      setDrawerFromDate(
                        newValue && newValue.isValid()
                          ? newValue.format("YYYY-MM-DD")
                          : ""
                      );
                      setDrawerDatePreset("Custom date");
                    }}
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                        placeholder: "DD-MM-YYYY",
                        sx: {
                          "& .MuiInputBase-root": {
                            height: "38px",
                            fontSize: "13px",
                            backgroundColor: "#fff",
                          },
                        },
                      },
                    }}
                  />
                </div>

                <div className="col-12 col-md-6 mb-3">
                  <label className="form-label">
                    End Date
                  </label>
                  <DatePicker
                    value={drawerToDate ? dayjs(drawerToDate) : null}
                    format="DD-MM-YYYY"
                    onChange={(newValue) => {
                      setDrawerToDate(
                        newValue && newValue.isValid()
                          ? newValue.format("YYYY-MM-DD")
                          : ""
                      );
                      setDrawerDatePreset("Custom date");
                    }}
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                        placeholder: "DD-MM-YYYY",
                        sx: {
                          "& .MuiInputBase-root": {
                            height: "38px",
                            fontSize: "13px",
                            backgroundColor: "#fff",
                          },
                        },
                      },
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Fixed Bottom Footer matching Project Filter */}
            <div className="bg-white position-absolute w-100 bottom-0 py-3 px-4">
              <div className="d-flex justify-content-start align-items-center">
                <button
                  type="submit"
                  className="add_btn me-3"
                  disabled={isEarningsFetching}
                >
                  {isEarningsFetching ? (
                    <div className="d-flex align-items-center gap-1">
                      <CircularProgress size={14} color="inherit" />
                      <span>Applying...</span>
                    </div>
                  ) : (
                    "Apply"
                  )}
                </button>
                <button
                  type="button"
                  className="cancel_btn"
                  onClick={onClose}
                >
                  Cancel
                </button>
              </div>
            </div>
          </form>
        </LocalizationProvider>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default ResourceEarningFilter;
